import { Injectable } from '@angular/core';
import { JWTTokenInterface, Order, User, USER_ROLES, UserRegister } from '../restaurant-manager.models';
import { ApiService, LibConfiguration } from './api.service';
import { map } from 'rxjs/operators';
import { BehaviorSubject, Observable } from 'rxjs';

@Injectable({
    providedIn: 'root'
})
export class UserService {
    static userLogged: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);

    constructor(protected apiService: ApiService) {
        UserService.loadUser();
    }

    static loadUser() {
        const user = localStorage.getItem('rm_user');
        const token = localStorage.getItem('rm_jwt_token');
        if (token && user) {
            ApiService.user = new User(JSON.parse(user));
            ApiService.token = token;
            ApiService.logged = true;
        }
        const client = localStorage.getItem('rm_client');
        if (client) {
            LibConfiguration.setClient(client);
        }
        UserService.userLogged.next(user !== null || false);
    }

    private static saveUser(user: User) {
        ApiService.user = user;
        localStorage.setItem('rm_user', JSON.stringify(user));
    }

    private static saveToken(token: JWTTokenInterface) {
        ApiService.logged = true;
        ApiService.token = token.token;
        localStorage.setItem('rm_jwt_token', token.token);

        if (token.hasOwnProperty('client') && token.client) {
            LibConfiguration.setClient(token.client);
            localStorage.setItem('rm_client', token.client);
        }
    }

    update(user: User) {
        return this.apiService
            .put<User>('/users/' + user.id, user, true)
            .pipe(
                map((u: any) => {
                    UserService.saveUser(u);
                    return new User(u);
                })
            );
    }

    assignCompany(companyCode: string): Observable<User> {
        return this.apiService.post<User>('/companies/assign', { code: companyCode }, true).pipe(map((user: User) => {
            return new User(user);
        }));
    }

    unlinkCompany(): Observable<User> {
        return this.apiService.post<User>('/companies/unlink', {}, true).pipe(map((user: User) => {
            return new User(user);
        }));
    }

    adminLogin(email: string, password: string, deviceId: string = null): Observable<JWTTokenInterface> {
        return this.apiService
            .post<JWTTokenInterface>('/api/login', { email, password, deviceId }, false, false)
            .pipe(map((token: JWTTokenInterface) => {
                UserService.saveToken(token);
                return token;
            }));
    }

    async adminLoginAndMe(email: string, password: string, deviceId: string = null): Promise<User> {
        return new Promise<User>((resolve, reject) => {
            this.adminLogin(email, password, deviceId).subscribe((response) => {
                this.me().subscribe(user => {
                    resolve(user);
                }, err => {
                    reject(err);
                });
            }, err => {
                reject(err);
            });
        });
    }

    async loginAndMe(email: string, password: string): Promise<User> {
        return new Promise<User>((resolve, reject) => {
            this.login(email, password).subscribe(token => {
                this.me().subscribe(user => {
                    resolve(user);
                }, err => {
                    reject(err);
                });
            }, err => {
                reject(err);
            });
        });
    }

    async registerLoginAndMe(userRegister: UserRegister): Promise<User> {
        return new Promise<User>((resolve, reject) => {
            this.register(userRegister).subscribe(async (user) => {
                this.loginAndMe(userRegister.email, userRegister.plainPassword).then((loggedUser) => {
                    resolve(loggedUser);
                }, err => {
                    reject(err);
                });
            }, err => {
                reject(err);
            });
        });
    }

    async logoutAndUnlinkUserLicence(): Promise<any> {
        return new Promise(async (resolve, reject) => {
            this.apiService.post<any>('/api/unlink-user-license', {}, true, false).subscribe((result) => {
                this.logout();
                resolve(result);
            }, err => {
                reject(err);
            });
        });
    }

    me(): Observable<User> {
        return this.apiService.get<User>('/me', true).pipe(map(user => {
            UserService.saveUser(new User(user));
            UserService.userLogged.next(true);
            return new User(user);
        }));
    }

    private login(email: string, password: string): Observable<JWTTokenInterface> {
        return this.apiService.post<JWTTokenInterface>('/login', { email, password }).pipe(map((token: JWTTokenInterface) => {
            UserService.saveToken(token);
            return token;
        }));
    }

    register(userRegister: UserRegister): Observable<User> {
        return this.apiService.post<User>('/register', userRegister).pipe(map((user) => {
            return new User(user);
        }));
    }

    isLogged(): boolean {
        return ApiService.logged && this.getUser() !== null;
    }

    isLoggedAsProUser(): boolean {
        return this.isLogged() && this.getUser() !== null && this.getUser().role === USER_ROLES.ROLE_PRO;
    }

    getUser(): User {
        return ApiService.user;
    }

    getUserById(id: string): Observable<User> {
        return this
            .apiService.get<Order>('/users/' + id, true)
            .pipe(map(result => new User(result)));
    }

    logout() {
        ApiService.logged = false;
        ApiService.user = null;
        ApiService.token = null;
        localStorage.removeItem('rm_user');
        localStorage.removeItem('rm_jwt_token');
        localStorage.removeItem('rm_client');
        UserService.userLogged.next(false);
    }

}
